import { useState, useEffect } from 'preact/hooks';
import {
  fetchStats,
  fetchSessions,
  fetchUsers,
  killSession,
  resumeSession,
  setSimulatedTier,
  setSimulatedBalance,
  type StatsData,
  type SessionRow,
  type UserRow,
} from '../api/admin';
import { TierQuotasTab } from './TierQuotasTab';
import { CreditPricingTab } from './CreditPricingTab';
import { AdminGrantsTab } from './AdminGrantsTab';
import { AkbKnowledgeTab } from './AkbKnowledgeTab';
import { IntentAnalyticsTab } from './IntentAnalyticsTab';
import { BillingTestTab } from './BillingTestTab';

type Tab = 'sessions' | 'users' | 'tiers' | 'pricing' | 'grants' | 'knowledge' | 'analytics' | 'wave' | 'billing';

interface WaveSettings {
  lighthouseModeEnabled: boolean;
  maxConcurrentAgents: number;
  waveExecutionEnabled: boolean;
}

export function AdminPage() {
  const [tab, setTab] = useState<Tab>('sessions');
  const [stats, setStats] = useState<StatsData | null>(null);
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [filterUserId, setFilterUserId] = useState('');
  const [error, setError] = useState('');
  const [waveSettings, setWaveSettings] = useState<WaveSettings | null>(null);

  useEffect(() => {
    fetchStats().then(setStats).catch(() => {});
    fetch('/api/admin/wave-settings')
      .then((r) => r.json())
      .then(setWaveSettings)
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === 'sessions') loadSessions();
    if (tab === 'users') fetchUsers().then(setUsers).catch(() => {});
  }, [tab, filterUserId]);

  function loadSessions() {
    const filters: Record<string, string> = {};
    if (filterUserId) filters.userId = filterUserId;
    fetchSessions(filters).then((r) => setSessions(r.sessions)).catch(() => {});
  }

  async function handleKill(id: string) {
    if (!confirm('Kill this session? The user will start fresh next time.')) return;
    try {
      await killSession(id);
      loadSessions();
      fetchStats().then(setStats);
    } catch (e: any) {
      setError(e.message);
    }
  }

  async function handleResume(id: string) {
    try {
      await resumeSession(id);
      alert('Session resumed. Your next voice command will continue this conversation.');
    } catch (e: any) {
      setError(e.message);
    }
  }

  return (
    <div class="app-container" style={{ maxWidth: 900 }}>
      <div class="top-bar">
        <h1>Admin</h1>
        <a
          href="/"
          style={{
            color: '#8888aa',
            fontSize: 12,
            textDecoration: 'none',
            border: '1px solid #4a4a6e',
            padding: '6px 12px',
            borderRadius: 6,
          }}
        >
          Back
        </a>
      </div>

      {/* Stats */}
      {stats && (
        <div class="admin-stats">
          <div class="stat-card">
            <div class="stat-value">{stats.activeSessions}</div>
            <div class="stat-label">Active</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">{stats.totalSessions}</div>
            <div class="stat-label">Total Sessions</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">${stats.totalCostUsd.toFixed(2)}</div>
            <div class="stat-label">Total Cost</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">{stats.totalMessages}</div>
            <div class="stat-label">Messages</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div class="admin-tabs">
        <button
          class={tab === 'sessions' ? 'active' : ''}
          onClick={() => setTab('sessions')}
        >
          Sessions
        </button>
        <button
          class={tab === 'users' ? 'active' : ''}
          onClick={() => setTab('users')}
        >
          Users
        </button>
        <button
          class={tab === 'tiers' ? 'active' : ''}
          onClick={() => setTab('tiers')}
        >
          Tiers
        </button>
        <button
          class={tab === 'pricing' ? 'active' : ''}
          onClick={() => setTab('pricing')}
        >
          Pricing
        </button>
        <button
          class={tab === 'grants' ? 'active' : ''}
          onClick={() => setTab('grants')}
        >
          Grants
        </button>
        <button
          class={tab === 'knowledge' ? 'active' : ''}
          onClick={() => setTab('knowledge')}
        >
          Knowledge
        </button>
        <button
          class={tab === 'analytics' ? 'active' : ''}
          onClick={() => setTab('analytics')}
        >
          Analytics
        </button>
        <button
          class={tab === 'billing' ? 'active' : ''}
          onClick={() => setTab('billing')}
        >
          Billing
        </button>
        {waveSettings?.lighthouseModeEnabled && (
          <button
            class={tab === 'wave' ? 'active' : ''}
            onClick={() => setTab('wave')}
          >
            Wave
          </button>
        )}
      </div>

      {error && (
        <div class="message error" style={{ margin: '8px 16px' }}>
          {error}
        </div>
      )}

      {/* Sessions Tab */}
      {tab === 'sessions' && (
        <div class="admin-content">
          {filterUserId && (
            <div style={{ padding: '8px 16px', fontSize: 12, color: '#8888aa' }}>
              Filtered by user: {filterUserId}{' '}
              <button
                style={{ background: 'none', border: 'none', color: '#6666cc', cursor: 'pointer', fontSize: 12 }}
                onClick={() => setFilterUserId('')}
              >
                clear
              </button>
            </div>
          )}
          {sessions.length === 0 ? (
            <div style={{ padding: 16, color: '#4a4a6e', textAlign: 'center' }}>
              No sessions found
            </div>
          ) : (
            sessions.map((s) => (
              <div key={s.id} class="admin-session-row">
                <div
                  class="admin-session-header"
                  onClick={() => setExpanded(expanded === s.id ? null : s.id)}
                >
                  <span class={`status-badge ${s.status}`}>{s.status}</span>
                  <span class="session-user">{s.userId.slice(0, 8)}...</span>
                  <span class="session-meta">
                    {s.numTurns} turns | ${(s.totalCostUsd || 0).toFixed(4)}
                  </span>
                  <span class="session-date">
                    {new Date(s.updatedAt).toLocaleString()}
                  </span>
                  <div class="session-actions">
                    {s.status === 'active' && (
                      <button class="btn-kill" onClick={(e) => { e.stopPropagation(); handleKill(s.id); }}>
                        Kill
                      </button>
                    )}
                    {s.claudeSessionId && (
                      <button class="btn-resume" onClick={(e) => { e.stopPropagation(); handleResume(s.id); }}>
                        Resume
                      </button>
                    )}
                  </div>
                </div>
                {expanded === s.id && s.messages && (
                  <div class="admin-messages">
                    {s.messages.map((m) => (
                      <div key={m.id} class={`message ${m.role}`}>
                        <span class="msg-time">
                          {new Date(m.createdAt).toLocaleTimeString()}
                        </span>
                        {m.text.length > 500 ? m.text.slice(0, 500) + '…' : m.text}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Users Tab */}
      {tab === 'users' && (
        <div class="admin-content">
          {users.map((u) => (
            <div key={u.userId} class="admin-user-row" style={{ flexWrap: 'wrap', gap: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', minWidth: 0 }}>
                <span class="user-email" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {u.email || u.userId.slice(0, 8)}
                </span>
                {u.tier && <span class="billing-badge tier">{u.tier}</span>}
                <select
                  value={u.simulatedTier ?? ''}
                  style={{ fontSize: 11, padding: '2px 4px', background: '#1a1a2e', color: '#8888aa', border: '1px solid #4a4a6e', borderRadius: 4, cursor: 'pointer' }}
                  onChange={async (e) => {
                    const val = (e.target as HTMLSelectElement).value;
                    try {
                      await setSimulatedTier(u.userId, val || null);
                      fetchUsers().then(setUsers).catch(() => {});
                    } catch { /* ignore */ }
                  }}
                >
                  <option value="">actual</option>
                  <option value="free">free</option>
                  <option value="pro">pro</option>
                  <option value="managed">managed</option>
                </select>
                <input
                  type="number"
                  min={0}
                  step={1}
                  placeholder="sim cr"
                  title="Simulated credit balance (centicredits) — blank = actual"
                  value={u.simulatedBalance ?? ''}
                  style={{ fontSize: 11, padding: '2px 4px', width: 68, background: '#1a1a2e', color: '#8888aa', border: '1px solid #4a4a6e', borderRadius: 4 }}
                  onChange={async (e) => {
                    const raw = (e.target as HTMLInputElement).value.trim();
                    const val = raw === '' ? null : Number(raw);
                    if (val !== null && (!Number.isInteger(val) || val < 0)) return;
                    try {
                      await setSimulatedBalance(u.userId, val);
                      fetchUsers().then(setUsers).catch(() => {});
                    } catch { /* ignore */ }
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: 4, width: '100%', flexWrap: 'wrap', alignItems: 'center' }}>
                <span class={`billing-badge ${u.claudeAuth === 'byok' ? 'green' : u.claudeAuth === 'sdk_oauth' ? 'amber' : 'red'}`}>
                  Claude: {u.claudeAuth === 'byok' ? 'BYOK' : u.claudeAuth === 'sdk_oauth' ? 'OAuth' : 'None'}
                </span>
                <span class={`billing-badge ${u.geminiAuth === 'byok_ai_studio' ? 'green' : u.geminiAuth === 'service_account' ? 'amber' : u.geminiAuth === 'operator_oauth' ? 'blue' : 'red'}`}>
                  Gemini: {u.geminiAuth === 'byok_ai_studio' ? 'BYOK' : u.geminiAuth === 'service_account' ? 'Svc Acct' : u.geminiAuth === 'operator_oauth' ? 'OAuth' : 'None'}
                </span>
                <span class="session-meta">
                  {u.totalSessions} sess | ${parseFloat(u.totalCostUsd || '0').toFixed(2)}
                </span>
              </div>
              <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
                <span class="session-date" style={{ fontSize: 11 }}>
                  {u.lastSeenAt ? new Date(u.lastSeenAt).toLocaleString() : 'never'}
                </span>
                <button
                  class="btn-resume"
                  onClick={() => { setFilterUserId(u.userId); setTab('sessions'); }}
                >
                  Sessions
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tiers Tab */}
      {tab === 'tiers' && <TierQuotasTab />}
      {tab === 'pricing' && <CreditPricingTab />}

      {/* Grants Tab */}
      {tab === 'grants' && <AdminGrantsTab />}

      {/* Knowledge Tab */}
      {tab === 'knowledge' && <AkbKnowledgeTab />}

      {/* Analytics Tab — intent distribution + transitions (model-meta-hardening Phase 7) */}
      {tab === 'analytics' && <IntentAnalyticsTab />}

      {tab === 'billing' && <BillingTestTab />}

      {/* Wave Tab — visible only when lighthouse_mode_enabled */}
      {tab === 'wave' && waveSettings && (
        <div class="admin-content" style={{ padding: '16px' }}>
          <h3 style={{ margin: '0 0 16px', color: '#c8c8e8', fontSize: 15 }}>Lighthouse Mode Settings</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#1a1a2e', borderRadius: 8 }}>
              <span style={{ color: '#8888aa', fontSize: 13 }}>Lighthouse Mode</span>
              <span style={{ color: waveSettings.lighthouseModeEnabled ? '#4caf50' : '#f44336', fontWeight: 600, fontSize: 13 }}>
                {waveSettings.lighthouseModeEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#1a1a2e', borderRadius: 8 }}>
              <span style={{ color: '#8888aa', fontSize: 13 }}>Wave Execution</span>
              <span style={{ color: waveSettings.waveExecutionEnabled ? '#4caf50' : '#f44336', fontWeight: 600, fontSize: 13 }}>
                {waveSettings.waveExecutionEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#1a1a2e', borderRadius: 8 }}>
              <span style={{ color: '#8888aa', fontSize: 13 }}>Max Concurrent Agents</span>
              <span style={{ color: '#c8c8e8', fontWeight: 600, fontSize: 15 }}>{waveSettings.maxConcurrentAgents}</span>
            </div>
            <p style={{ color: '#5a5a7e', fontSize: 11, margin: '4px 0 0', lineHeight: 1.5 }}>
              These values are set in the installation profile YAML and are read-only at runtime.
              To change them, update <code>backend/config/profiles/prod.yaml</code> and redeploy.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
